import { FC, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Heading } from '@/components/shared/Heading';
import { PageContent } from '@/components/shared/PageContent';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';
import { Check, ChevronsUpDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FilterEmpty } from '@/components/shared/FilterEmpty';
import { DateRange } from 'react-day-picker';
import useCustomLoader from '@/hooks/useCustomLoader';
import { useGetMonthlyExpenseReport } from '@/api/user/user.api';
import {
  useGetAccountingReport,
  useGetAccountingReportByLedgerAccount,
  useGetJournalBookReport,
  useGetLedgerBookPdfReport,
  useGetLedgerBookXlsxReport,
} from '@/api/accountingEntry/accountingEntry.api';
import { DATES_OPTIONS } from '@/common/constants';
import { useLedgerAccountChildrens } from '@/api/ledgerAccount/ledgerAccount.api';
import DateRangePicker from '@/components/shared/DateRangePicker';
import SingleDatePicker from '@/components/shared/SingleDatePicker';
import { cn } from '@/lib/utils';

const PAGE_TITLE = 'Reportes';

interface DatePickerFieldProps {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  fromDate?: Date | undefined;
  toDate?: Date | undefined;
}

const DatePickerField: FC<DatePickerFieldProps> = ({
  label,
  value,
  onChange,
  fromDate,
  toDate,
}) => {
  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <SingleDatePicker
        value={value ?? undefined}
        onChange={(date) => onChange(date ?? null)}
        fromDate={fromDate}
        toDate={toDate}
      />
    </div>
  );
};

interface DateRangePickerFieldProps {
  label: string;
  value: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
}

const DateRangePickerField: FC<DateRangePickerFieldProps> = ({ label, value, onChange }) => {
  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <DateRangePicker value={value} onChange={onChange} />
    </div>
  );
};

const Reports: FC = () => {
  const [monthlyRange, setMonthlyRange] = useState<DateRange | undefined>();
  const [monthlyDateType, setMonthlyDateType] = useState('accountingDate');

  const [generalLedgerDate, setGeneralLedgerDate] = useState<Date | null>(null);

  const [ledgerAccountId, setLedgerAccountId] = useState('');
  const [ledgerRange, setLedgerRange] = useState<DateRange | undefined>();

  const [journalRange, setJournalRange] = useState<DateRange | undefined>();
  const [ledgerBookRange, setLedgerBookRange] = useState<DateRange | undefined>();

  const { data: ledgerAccounts = [], isLoading: isLoadingLedgerAccounts } =
    useLedgerAccountChildrens();

  const sortedLedgerAccounts = useMemo(() => {
    return [...ledgerAccounts].sort((a, b) => a.name.localeCompare(b.name));
  }, [ledgerAccounts]);

  const monthlyReport = useCustomLoader(useGetMonthlyExpenseReport());

  const accountingParams = useMemo(() => {
    const params = new URLSearchParams();
    if (generalLedgerDate) {
      params.set('date', format(generalLedgerDate, 'yyyy-MM-dd'));
    }
    return params;
  }, [generalLedgerDate]);
  const generalLedgerReport = useCustomLoader(useGetAccountingReport(accountingParams));

  const ledgerAccountParams = useMemo(() => {
    const params = new URLSearchParams();
    if (ledgerRange?.from) {
      params.set('startDate', format(ledgerRange.from, 'yyyy-MM-dd'));
    }
    if (ledgerRange?.to) {
      params.set('endDate', format(ledgerRange.to, 'yyyy-MM-dd'));
    }
    return params;
  }, [ledgerRange]);
  const ledgerAccountReport = useCustomLoader(
    useGetAccountingReportByLedgerAccount(ledgerAccountId, ledgerAccountParams),
  );
  const journalBookReport = useCustomLoader(useGetJournalBookReport());
  const ledgerBookPdfReport = useCustomLoader(useGetLedgerBookPdfReport());
  const ledgerBookXlsxReport = useCustomLoader(useGetLedgerBookXlsxReport());

  const handleDownloadMonthlyReport = () => {
    monthlyReport.response({
      startDate: monthlyRange?.from || undefined,
      endDate: monthlyRange?.to || undefined,
      dateType: monthlyDateType,
    });
  };

  const handleDownloadGeneralLedgerReport = () => {
    generalLedgerReport.response();
  };

  const handleDownloadLedgerAccountReport = () => {
    if (!ledgerAccountId) {
      toast.error('Seleccioná una cuenta contable para descargar este reporte.');
      return;
    }
    ledgerAccountReport.response();
  };

  const handleDownloadJournalBookReport = () => {
    if (!journalRange?.from || !journalRange?.to) {
      toast.error('Seleccioná fecha de inicio y fecha de fin para descargar el Libro Diario.');
      return;
    }

    journalBookReport.response({
      startDate: journalRange.from,
      endDate: journalRange.to,
    });
  };

  const handleDownloadLedgerBookPdfReport = () => {
    if (!ledgerBookRange?.from || !ledgerBookRange?.to) {
      toast.error('Seleccioná fecha de inicio y fecha de fin para descargar el Libro Mayor.');
      return;
    }

    ledgerBookPdfReport.response({
      startDate: ledgerBookRange.from,
      endDate: ledgerBookRange.to,
    });
  };

  const handleDownloadLedgerBookXlsxReport = () => {
    if (!ledgerBookRange?.from || !ledgerBookRange?.to) {
      toast.error('Seleccioná fecha de inicio y fecha de fin para descargar el Libro Mayor.');
      return;
    }

    ledgerBookXlsxReport.response({
      startDate: ledgerBookRange.from,
      endDate: ledgerBookRange.to,
    });
  };

  return (
    <>
      <Helmet>
        <title>PEM - {PAGE_TITLE}</title>
      </Helmet>
      <Heading
        title={PAGE_TITLE}
        subtitle="Descarga centralizada de reportes disponibles"
        classNames={{ title: 'text-4xl', subtitle: 'text-xl font-normal text-muted-foreground' }}
      />
      <PageContent>
        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Reporte Integral Mensual</CardTitle>
              <CardDescription>
                Incluye facturas y asientos manuales en un único archivo.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por</label>
                <Select onValueChange={setMonthlyDateType} value={monthlyDateType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione tipo de fecha" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATES_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DateRangePickerField
                label="Período"
                value={monthlyRange}
                onChange={setMonthlyRange}
              />
              <div className="md:col-span-2 flex justify-end">
                <Button
                  variant="default"
                  onClick={handleDownloadMonthlyReport}
                  isLoading={monthlyReport.isLoading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Mayor de Cuentas (General)</CardTitle>
              <CardDescription>
                Descarga el mayor general de cuentas con corte a una fecha.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <DatePickerField
                label="Fecha de corte (opcional)"
                value={generalLedgerDate}
                onChange={setGeneralLedgerDate}
              />
              <div className="md:col-span-2 flex items-end justify-end">
                <Button
                  variant="default"
                  onClick={handleDownloadGeneralLedgerReport}
                  isLoading={generalLedgerReport.isLoading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Libro Diario (PDF)</CardTitle>
              <CardDescription>
                Descarga el detalle de asientos por período, con total general Debe/Haber.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <DateRangePickerField
                label="Período"
                value={journalRange}
                onChange={setJournalRange}
              />
              <div className="flex items-end justify-end">
                <Button
                  variant="default"
                  onClick={handleDownloadJournalBookReport}
                  isLoading={journalBookReport.isLoading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Libro Mayor (PDF y Excel)</CardTitle>
              <CardDescription>
                Detalle de movimientos Debe/Haber por cuenta contable, con saldo inicial y saldo
                acumulado.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <DateRangePickerField
                label="Período"
                value={ledgerBookRange}
                onChange={setLedgerBookRange}
              />
              <div className="flex items-end justify-end gap-2 flex-wrap">
                <Button
                  variant="default"
                  onClick={handleDownloadLedgerBookPdfReport}
                  isLoading={ledgerBookPdfReport.isLoading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar PDF
                </Button>
                <Button
                  variant="default"
                  onClick={handleDownloadLedgerBookXlsxReport}
                  isLoading={ledgerBookXlsxReport.isLoading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar Excel
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Mayor por Cuenta Contable</CardTitle>
              <CardDescription>
                Descarga el mayor detallado de una cuenta contable específica.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="w-full md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cuenta contable
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      disabled={isLoadingLedgerAccounts}
                      className={cn(
                        'w-full justify-between font-normal',
                        !ledgerAccountId && 'text-muted-foreground',
                      )}
                    >
                      <span className="truncate">
                        {ledgerAccountId
                          ? sortedLedgerAccounts.find((a) => a.id === ledgerAccountId)?.name
                          : 'Seleccione una cuenta'}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 popover-content-width-same-as-its-trigger">
                    <Command>
                      <CommandInput
                        placeholder="Buscar cuenta contable..."
                        className="placeholder:font-light"
                      />
                      <ScrollArea
                        className={`overflow-auto ${sortedLedgerAccounts.length > 6 ? 'h-52' : ''}`}
                      >
                        <CommandEmpty>
                          <FilterEmpty title="No se encontraron resultados que coincidan con la búsqueda ingresada" />
                        </CommandEmpty>
                        <CommandGroup>
                          {sortedLedgerAccounts.map((account) => (
                            <CommandItem
                              value={account.name}
                              key={account.id}
                              onSelect={() => setLedgerAccountId(account.id)}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  account.id === ledgerAccountId ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              {account.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </ScrollArea>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <DateRangePickerField
                label="Período (opcional)"
                value={ledgerRange}
                onChange={setLedgerRange}
              />

              <div className="flex items-end justify-end">
                <Button
                  variant="default"
                  onClick={handleDownloadLedgerAccountReport}
                  isLoading={ledgerAccountReport.isLoading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </>
  );
};

export default Reports;
